import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { useModal } from './ModalContext';

const SessionTimerContext = createContext();

export function SessionTimerProvider({
  children,
  warnBelowPercent = 20,      // dispara alerta quando ≤ 20% do tempo restante
  warnBelowSeconds = 5 * 60,   // ou quando ≤ 5 minutos
  tickMs = 800,               // atualização a cada 0.8s
  autoModal = true,            // abre modal automaticamente
}) {
  const { issuedAt, expiresAt, secondsLeft, token, renewToken, tipoUsuario } = useAuth();
  const { showModal } = useModal();
  const { t, i18n } = useTranslation();
  const language = tipoUsuario === 'participante' ? i18n.resolvedLanguage : 'pt-BR';

  const [showWarning, setShowWarning] = useState(false);
  const [silencedOnce, setSilencedOnce] = useState(false);
  const lastWarnReasonRef = useRef(null);

  // totais e restante (em segundos)
  const totalS = useMemo(() => {
    if (!issuedAt || !expiresAt) return null;
    return Math.max(0, expiresAt - issuedAt);
  }, [issuedAt, expiresAt]);

  const leftS = useMemo(() => {
    if (typeof secondsLeft !== 'number') return null;
    return Math.max(0, secondsLeft);
  }, [secondsLeft]);

  // progress 1-0 e percent 100-0
  const progress = useMemo(() => {
    if (!totalS || leftS == null || totalS === 0) return null;
    return Math.min(1, Math.max(0, leftS / totalS));
  }, [leftS, totalS]);

  const percent = useMemo(() => (progress == null ? null : Math.round(progress * 100)), [progress]);

  // Lógica de warning + modal
  useEffect(() => {
    if (!token || leftS == null || totalS == null || totalS === 0) {
      setShowWarning(false);
      lastWarnReasonRef.current = null;
      return;
    }

    const hitByPercent = percent != null && percent <= warnBelowPercent;
    const hitBySeconds = leftS <= warnBelowSeconds;
    const shouldWarn = (hitByPercent || hitBySeconds) && !silencedOnce;

    if (shouldWarn) {
      setShowWarning(true);
      lastWarnReasonRef.current = hitBySeconds ? 'seconds' : 'percent';

      if (autoModal) {
        const minutes = Math.max(0, Math.floor(leftS / 60));
        showModal(
          t('session.expiringTitle', { lng: language }),
          minutes > 0
            ? t('session.expiresSoon', {
                lng: language,
                time: t('session.minutes', { lng: language, count: minutes }),
              })
            : t('session.expiresVerySoon', { lng: language }),
          // onConfirm:
          async () => { await renewToken(); setShowWarning(false); setSilencedOnce(false); },
          // onCancel:
          () => { setSilencedOnce(true); }
        );
      }
    } else {
      if (percent && percent > warnBelowPercent && leftS > warnBelowSeconds) {
        setSilencedOnce(false);
      }

      setShowWarning(false);
      lastWarnReasonRef.current = null;
    }
  }, [token, leftS, percent, warnBelowPercent, warnBelowSeconds, autoModal, showModal, renewToken, silencedOnce, totalS, t, language]);

  const silenceWarningOnce = () => setSilencedOnce(true);
  const renewNow = async () => {
    const r = await renewToken();
    if (r?.success) {
      setShowWarning(false);
      setSilencedOnce(false);
    }
    return r;
  };

  // tick
  useEffect(() => {
    if (!token || !expiresAt) return;
    const id = setInterval(() => {}, tickMs);
    return () => clearInterval(id);
  }, [token, expiresAt, tickMs]);

  return (
    <SessionTimerContext.Provider
      value={{
        progress,            // 0..1
        percent,             // 0..100
        timeLeft: leftS,     // s
        timeTotal: totalS,   // s

        // estado de aviso
        showWarning,
        warnReason: lastWarnReasonRef.current,

        // ações
        renewNow,
        silenceWarningOnce,
      }}
    >
      {children}
    </SessionTimerContext.Provider>
  );
}

export function useSessionTimer() {
  return useContext(SessionTimerContext);
}
